import React, { useCallback, useEffect, useState } from "react";
import {
    Alert,
    Box,
    Chip,
    CircularProgress,
    Pagination,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
} from "@mui/material";
import BillingPageFrame from "./BillingPageFrame";
import { listTopups } from "./billingApi";
import { formatCreditCount, formatRolesForDisplay, formatRupeesFromPaisa, formatWhen } from "./billingFormat";
import { useBilling } from "../../context/BillingContext";

const STATUS_COLOR = {
    SUCCESS: "success",
    PENDING: "warning",
    FAILED: "error",
};

const CreditTopupsPage = () => {
    const { isCreditBased, loading: billingLoading } = useBilling();
    const [page, setPage] = useState(1);
    const [rows, setRows] = useState([]);
    const [lastPage, setLastPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const result = await listTopups({ page, per_page: 20 });
            setRows(result.rows);
            setLastPage(result.lastPage);
        } catch (err) {
            setRows([]);
            setError(err?.status === 403
                ? "Only the account admin can view top-ups."
                : (err?.message || "Could not load top-ups"));
        } finally {
            setLoading(false);
        }
    }, [page]);

    useEffect(() => {
        if (billingLoading || !isCreditBased) {
            if (!billingLoading) setLoading(false);
            return;
        }
        load();
    }, [billingLoading, isCreditBased, load]);

    return (
        <BillingPageFrame
            title="Top-ups"
            subtitle="Payments that added credits. Online checkouts and credits added by the system are listed here."
        >
            {!billingLoading && !isCreditBased ? (
                <Alert severity="info">Top-ups are available on credit accounts. Your balance is under Credits Information.</Alert>
            ) : (
                <>
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                    {loading ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress size={28} /></Box>
                    ) : rows.length === 0 ? (
                        <Alert severity="info">No top-ups yet. Credits you buy, or that an admin adds, appear here.</Alert>
                    ) : (
                        <Box sx={{ overflowX: "auto", border: "1px solid #e5e7eb", borderRadius: 2 }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        {["When", "Amount", "Mode", "Status", "Reference"].map((heading) => (
                                            <TableCell key={heading} sx={{ fontWeight: 700 }}>{heading}</TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {rows.map((row) => {
                                        const roles = formatRolesForDisplay(row.user_role || row.user?.user_role);
                                        return (
                                        <TableRow key={row.id}>
                                            <TableCell>{formatWhen(row.created_at || row.updated_at)}</TableCell>
                                            <TableCell>
                                                {formatRupeesFromPaisa(row.credit_amount)}
                                                <Box component="span" sx={{ display: "block", color: "text.secondary", fontSize: "0.75rem" }}>
                                                    {formatCreditCount(row.credit_amount)}
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                {row.payment_mode === "ADMIN" ? "Added by system" : "Online payment"}
                                                {roles && (
                                                    <Box component="span" sx={{ display: "block", color: "text.secondary", fontSize: "0.75rem" }}>
                                                        {roles}
                                                    </Box>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    size="small"
                                                    label={row.payment_status || "—"}
                                                    color={STATUS_COLOR[row.payment_status] || "default"}
                                                />
                                            </TableCell>
                                            <TableCell>{row.payment_gateway_id || "—"}</TableCell>
                                        </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </Box>
                    )}
                    {lastPage > 1 && (
                        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                            <Pagination count={lastPage} page={page} onChange={(_, value) => setPage(value)} />
                        </Box>
                    )}
                </>
            )}
        </BillingPageFrame>
    );
};

export default CreditTopupsPage;
